/**
 * Shared jsPDF letterhead band used by every portal PDF download.
 * loadLetterhead(jobNumber) resolves via GET /api/letterhead?jobNumber=
 * drawLetterhead(doc, letterhead, opts) draws logo + legal block + job line + type + rule.
 *
 * Callers MUST place titles/tables at or below the returned Y. Dense landscape
 * reports (Maestro consolidated / roll-up) use compact:true + contentStartBelowLetterhead()
 * so page-1 content clears the band while continuation pages stay compact.
 */
import { authenticatedApiCall } from './apiClient';
import { displayProjectType, jobLine, loadNomenclature } from './nomenclature';

const DEFAULT_LOGO = '/lgl-logo-cropped.png';

/**
 * Vertical offsets for content that sits under an optional letterhead band.
 * Without letterheadY, returns the compact top used on continuation pages.
 *
 * @param {number|null|undefined} letterheadY  Y returned by applyLetterhead / drawLetterhead
 * @param {{ margin?: number, titleGap?: number }} [opts]
 * @returns {{ titleY: number, firstPageTop: number, contTop: number }}
 */
export function contentStartBelowLetterhead(letterheadY, opts = {}) {
  const margin = opts.margin ?? 7;
  // Gap from title baseline down to the table top (title is ~3–4mm tall at 10pt).
  const titleGap = opts.titleGap ?? 7;
  const contTop = margin + 4;
  if (letterheadY == null || !Number.isFinite(letterheadY)) {
    // Pre-letterhead rhythm: title just under the top margin, table a few mm below.
    return { titleY: margin + 2, firstPageTop: margin + 8, contTop };
  }
  const titleY = letterheadY + 1;
  return { titleY, firstPageTop: titleY + titleGap, contTop };
}

async function imageToDataUrl(src) {
  if (!src) return null;
  if (src.startsWith('data:')) return src;
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = reject;
    img.src = src;
  });
}

export async function loadLetterhead({ instance, accounts, jobNumber, projectId } = {}) {
  const params = new URLSearchParams();
  if (jobNumber) params.set('jobNumber', jobNumber);
  else if (projectId) params.set('projectId', projectId);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const api = (method, url, data) => authenticatedApiCall(instance, accounts, method, url, data);
  try {
    await loadNomenclature(api);
  } catch (_) { /* defaults */ }
  const res = await api('GET', `/letterhead${qs}`);
  return res.data?.data || null;
}

/**
 * Draw the standard letterhead. Returns the next Y (mm) below the hairline.
 *
 * @param {import('jspdf').jsPDF} doc
 * @param {object} resolved  result of loadLetterhead (or { letterhead, context })
 * @param {object} opts { jobNumber, jobName, clientName, projectType, omitJobLine, margin, startY, compact }
 *   compact — slim band for dense landscape tables (smaller logo/type, skip blank project type)
 */
export async function drawLetterhead(doc, resolved, opts = {}) {
  const letterhead = resolved?.letterhead || resolved || {};
  const context = resolved?.context || {};
  const compact = !!opts.compact;
  const margin = opts.margin ?? (compact ? 8 : 15);
  const pageWidth = doc.internal.pageSize.getWidth();
  let y = opts.startY ?? (compact ? 9 : 14);

  const jobNumber = opts.jobNumber ?? context.job_number;
  const jobName = opts.jobName ?? context.job_name;
  const clientName = opts.clientName ?? context.client_name;
  const projectType = opts.projectType ?? context.project_type;
  const omitJobLine = opts.omitJobLine || (!jobNumber && !jobName);

  const logoSrc = letterhead.logo_data_url || DEFAULT_LOGO;
  const logoW = compact ? 10 : 16;
  const logoTop = y - (compact ? 3 : 6);
  try {
    const dataUrl = await imageToDataUrl(logoSrc);
    if (dataUrl) doc.addImage(dataUrl, 'PNG', margin, logoTop, logoW, logoW);
  } catch (e) {
    console.warn('drawLetterhead: logo skipped', e);
  }

  const textX = margin + logoW + (compact ? 3 : 4);
  doc.setTextColor(20, 20, 20);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(compact ? 9 : 12);
  doc.text(letterhead.legal_name || 'Laframboise Group Ltd.', textX, y + (compact ? 1 : 2));
  y += compact ? 3.5 : 5;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(compact ? 7 : 8);
  if (letterhead.address_line1) {
    doc.text(String(letterhead.address_line1), textX, y);
    y += compact ? 3 : 3.5;
  }
  if (!compact && letterhead.address_line2) {
    doc.text(String(letterhead.address_line2), textX, y);
    y += 3.5;
  }
  const contact = [letterhead.phone, letterhead.email].filter(Boolean).join('   ');
  if (!compact && contact) {
    doc.text(contact, textX, y);
    y += 5;
  } else if (!compact) {
    y += 2;
  } else {
    y += 1.5;
  }

  // Job line must clear the logo bottom — logo sits at logoTop..logoTop+logoW.
  y = Math.max(y, logoTop + logoW + (compact ? 4 : 3));
  if (!omitJobLine) {
    const line = jobLine({ job: jobNumber, client: clientName, jobName });
    if (line) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(compact ? 8 : 9.5);
      doc.text(line, margin, y);
      y += compact ? 3.5 : 5;
    }
    const typeLabel = displayProjectType(projectType);
    // Skip the placeholder "..." on compact dense reports — it reads as a glitch.
    if (!compact || (typeLabel && typeLabel !== '...')) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(compact ? 7.5 : 9);
      doc.text(typeLabel, margin, y);
      y += compact ? 3.5 : 5;
    }
  }

  y += compact ? 0.5 : 1;
  doc.setDrawColor(40);
  doc.setLineWidth(0.35);
  doc.line(margin, y, pageWidth - margin, y);
  return y + (compact ? 3.5 : 6);
}

/**
 * Convenience: load + draw. Returns { y, resolved }.
 */
export async function applyLetterhead(doc, {
  instance, accounts, jobNumber, projectId, jobName, clientName, projectType,
  omitJobLine, margin, startY, compact,
} = {}) {
  const resolved = await loadLetterhead({ instance, accounts, jobNumber, projectId });
  const y = await drawLetterhead(doc, resolved, {
    jobNumber: jobNumber || resolved?.context?.job_number,
    jobName: jobName || resolved?.context?.job_name,
    clientName: clientName || resolved?.context?.client_name,
    projectType: projectType || resolved?.context?.project_type,
    omitJobLine, margin, startY, compact,
  });
  return { y, resolved };
}
